import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Globe, Loader2, BadgeCheck, ArrowLeft, Calendar, Facebook, Instagram, Youtube } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SmartImage from '../components/SmartImage';
import ShareButton from '../components/ShareButton';
import api, { mediaUrl } from '../lib/api';
import { sanitizeHtml } from '../lib/sanitize';
import { typeLabel, dayLabel } from '../lib/centerOptions';

const CenterDetail = () => {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/centers/${slug}`)
      .then((res) => setC(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="App"><Header /><div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-mahogany" /></div><Footer /></div>;
  if (notFound || !c) return (
    <div className="App"><Header />
      <div className="text-center py-32 bg-cream">
        <p className="text-mahogany/70">Center not found.</p>
        <Link to="/centers" className="pill-btn mt-4 inline-flex">Back to Directory</Link>
      </div><Footer />
    </div>
  );

  const social = c.social || {};
  return (
    <div className="App">
      <Header />
      <main className="bg-cream">
        <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-10">
          <Link to="/centers" className="inline-flex items-center gap-2 text-mahogany/70 hover:text-mahogany mb-6" data-testid="center-back-link"><ArrowLeft className="w-4 h-4" /> All Centers</Link>

          <SmartImage src={mediaUrl(c.cover_image)} alt={c.name} className="aspect-[16/7] rounded-3xl overflow-hidden">
            <div className="absolute top-4 left-4 z-10 rounded-full bg-cream/90 text-mahogany text-xs uppercase tracking-widest px-3 py-1">{typeLabel(c.type)}</div>
          </SmartImage>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <h1 className="font-serif-display text-4xl md:text-5xl text-burgundy" data-testid="center-detail-name">{c.name}</h1>
            {c.is_verified && <span className="rounded-full bg-green-600 text-white text-xs px-3 py-1 flex items-center gap-1"><BadgeCheck className="w-4 h-4" /> Verified</span>}
            {c.neo_sannyas_based && <span className="rounded-full bg-rose-soft text-mahogany text-xs px-3 py-1">Neo-Sannyas</span>}
            <ShareButton title={c.name} text={`Discover ${c.name} on Osho Universal`} testid="share-center" className="ml-auto" />
          </div>

          {c.short_description && <p className="mt-3 text-lg text-burgundy/80">{c.short_description}</p>}

          <div className="mt-8 grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2 min-w-0 space-y-6">
              {c.description && <div className="prose-osho text-burgundy/85 leading-relaxed break-words max-w-full" dangerouslySetInnerHTML={{ __html: sanitizeHtml(c.description) }} />}

              {c.gallery?.length > 0 && (
                <div>
                  <h2 className="font-serif-display text-2xl text-burgundy mb-3">Gallery</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="center-gallery">
                    {c.gallery.map((g, i) => <SmartImage key={i} src={mediaUrl(g)} alt="" className="rounded-xl aspect-square" />)}
                  </div>
                </div>
              )}

              {c.programs_offered?.length > 0 && (
                <div>
                  <h2 className="font-serif-display text-2xl text-burgundy mb-3">Programs Offered</h2>
                  <div className="flex flex-wrap gap-2">
                    {c.programs_offered.map((p) => <span key={p} className="text-sm px-3 py-1 rounded-full bg-rose-soft text-mahogany">{p}</span>)}
                  </div>
                </div>
              )}

              {c.facilities?.length > 0 && (
                <div>
                  <h2 className="font-serif-display text-2xl text-burgundy mb-3">Facilities</h2>
                  <div className="flex flex-wrap gap-2">
                    {c.facilities.map((p) => <span key={p} className="text-sm px-3 py-1 rounded-full bg-white/70 border border-rose/40 text-burgundy/80">{p}</span>)}
                  </div>
                </div>
              )}

              {c.initiation_lineage && (
                <div>
                  <h2 className="font-serif-display text-2xl text-burgundy mb-2">Initiation Lineage</h2>
                  <p className="text-burgundy/85">{c.initiation_lineage}</p>
                </div>
              )}
            </div>

            <aside className="min-w-0 space-y-3">
              <div className="p-5 rounded-2xl bg-white/70 border border-rose/40 space-y-3">
                {(c.location?.address || c.location?.city) && (
                  <Row icon={MapPin}>{[c.location?.address, c.location?.city, c.location?.state, c.location?.country, c.location?.pincode].filter(Boolean).join(', ')}</Row>
                )}
                {c.location?.google_maps_url && <a href={c.location.google_maps_url} target="_blank" rel="noreferrer" className="text-sm text-rose-italic hover:underline block">View on Google Maps →</a>}
                {c.contact_phone && <Row icon={Phone}><a href={`tel:${c.contact_phone}`} className="hover:underline">{c.contact_phone}</a></Row>}
                {c.contact_email && <Row icon={Mail}><a href={`mailto:${c.contact_email}`} className="hover:underline break-all">{c.contact_email}</a></Row>}
                {social.website && <Row icon={Globe}><a href={social.website} target="_blank" rel="noreferrer" className="hover:underline break-all">Website</a></Row>}
                {c.year_established && <Row icon={Calendar}>Established {c.year_established}</Row>}
                {c.contact_person && <div className="text-sm text-mahogany/70 pt-1">Contact: {c.contact_person}</div>}
                <div className="flex gap-3 pt-1">
                  {social.facebook && <a href={social.facebook} target="_blank" rel="noreferrer" className="text-mahogany/70 hover:text-mahogany"><Facebook className="w-5 h-5" /></a>}
                  {social.instagram && <a href={social.instagram} target="_blank" rel="noreferrer" className="text-mahogany/70 hover:text-mahogany"><Instagram className="w-5 h-5" /></a>}
                  {social.youtube && <a href={social.youtube} target="_blank" rel="noreferrer" className="text-mahogany/70 hover:text-mahogany"><Youtube className="w-5 h-5" /></a>}
                </div>
              </div>

              {c.hours?.length > 0 && (
                <div className="p-5 rounded-2xl bg-white/70 border border-rose/40" data-testid="center-detail-hours">
                  <div className="font-serif-display text-lg text-burgundy mb-2">Hours of Operation</div>
                  <ul className="space-y-1">
                    {c.hours.map((h) => (
                      <li key={h.day} className="flex items-center justify-between text-sm">
                        <span className="text-burgundy/85">{dayLabel(h.day)}</span>
                        <span className={h.closed ? 'text-red-500' : 'text-mahogany/80'}>
                          {h.closed ? 'Closed' : `${h.open || '—'} – ${h.close || '—'}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </aside>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

const Row = ({ icon: Icon, children }) => (
  <div className="flex items-start gap-2 text-sm text-burgundy/85"><Icon className="w-4 h-4 text-rose-italic mt-0.5 shrink-0" /> <span>{children}</span></div>
);

export default CenterDetail;
